import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { joinCss, pickCss } from '@/shared/lib/css-utils';
import css from './chrome.module.scss';

const s = {
  footer: pickCss(css, css.footer),
  footerSpacer: pickCss(css, css.footerSpacer),
  footerCaption: pickCss(css, css.footerCaption),
  btn: pickCss(css, css.btn),
  btnPrimary: pickCss(css, css.btnPrimary),
  btnOutline: pickCss(css, css.btnOutline),
  btnSm: pickCss(css, css.btnSm),
  btnXs: pickCss(css, css.btnXs),
};

// ── LIVE BRIEF bar (design: 42–48px, stroke bottom, brief chip + summary + Edit) ──

type LiveBriefBarProps = {
  summary: string;
  onEdit?: () => void;
};

export const LiveBriefBar = ({ summary, onEdit }: LiveBriefBarProps) => (
  <div className={css.liveBrief}>
    <span className={css.liveBriefChip}>LIVE BRIEF</span>
    <span className={css.liveBriefSummary}>{summary}</span>
    {onEdit && (
      <button className={css.liveBriefEdit} onClick={onEdit} type="button">
        Edit
      </button>
    )}
  </div>
);

// ── Screen footer (design: 72–80px, stroke top, two buttons + caption) ──

type ScreenFooterProps = {
  caption?: string;
  outlineLabel: string;
  primaryLabel: string;
  onOutline: () => void;
  onPrimary: () => void;
  primaryDisabled?: boolean;
  primaryIcon?: ReactNode;
  primaryTestId?: string;
  outlineTestId?: string;
};

export const ScreenFooter = ({
  caption,
  outlineLabel,
  primaryLabel,
  onOutline,
  onPrimary,
  primaryDisabled,
  primaryIcon,
  primaryTestId,
  outlineTestId,
}: ScreenFooterProps) => (
  <footer className={s.footer()}>
    <div className={css.footerRow}>
      <RecButton
        data-testid={outlineTestId}
        onClick={onOutline}
        variant="outline"
      >
        {outlineLabel}
      </RecButton>
      <span className={s.footerSpacer()} />
      <RecButton
        data-testid={primaryTestId}
        disabled={primaryDisabled}
        onClick={onPrimary}
        variant="primary"
      >
        {primaryIcon}
        {primaryLabel}
      </RecButton>
    </div>
    {caption && <span className={s.footerCaption()}>{caption}</span>}
  </footer>
);

// ── Buttons ────────────────────────────────────────────────────────

type RecButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant: 'primary' | 'outline';
  size?: 'sm' | 'xs' | 'default';
};

const RecButton = ({
  children,
  className,
  variant,
  size = 'default',
  ...rest
}: RecButtonProps) => (
  <button
    className={joinCss(
      s.btn(),
      variant === 'primary' ? s.btnPrimary() : s.btnOutline(),
      size === 'sm' && s.btnSm(),
      size === 'xs' && s.btnXs(),
      className
    )}
    type="button"
    {...rest}
  >
    {children}
  </button>
);
